import { statusBadge } from "./ticketStatus.tsx";
import { StatusIcon } from "../../components/ui/statusIcon.tsx";
import { CLOSED_STATUS, type TicketHistory } from "../../api/ticket.ts";
import styles from "./ticketHistoryTimeline.module.scss";

const ESCALATION_PREFIX = "Escalated to Zendesk";
const ESCALATION_RE = /#(\d+)\)\s*-\s*(https?:\/\/\S+)/;

function parseEscalation(notes: string): { ticketNumber: string; url: string } | null {
  if (!notes.startsWith(ESCALATION_PREFIX)) return null;
  const match = notes.match(ESCALATION_RE);
  if (!match) return null;
  return { ticketNumber: match[1]!, url: match[2]! };
}

/** Highlighted callout for a ticket's Solution field — shared look wherever it's shown. */
export function SolutionCallout({ solution }: { solution: string | null | undefined }) {
  if (!solution) return null;
  return (
    <div className={styles.solutionCallout}>
      <StatusIcon kind="solved" size={18} />
      <div className={styles.solutionBody}>
        <div className={styles.solutionLabel}>Solution</div>
        <div className={styles.solutionText}>{solution}</div>
      </div>
    </div>
  );
}

/**
 * Shared history timeline — a connected vertical line with a marker per event.
 * Used by both the kiosk "Problems" tab and the standalone Tickets page so the
 * two never drift apart.
 *
 * `formatDate` is injected rather than baked in: the two callers format dates
 * differently (browser-local vs. server-timezone-aware via util/serverTime.ts)
 * and this component shouldn't force one convention on the other.
 */
export function TicketHistoryTimeline({
  history,
  formatDate,
  emptyMessage = "No history events.",
}: {
  history: TicketHistory[] | null;
  formatDate: (d: string | Date | null | undefined) => string;
  emptyMessage?: string;
}) {
  if (history === null) {
    return <p className={styles.hint}>Loading history…</p>;
  }
  if (history.length === 0) {
    return <p className={styles.hint}>{emptyMessage}</p>;
  }

  return (
    <div className={styles.timeline}>
      {history.map((h) => {
        const resolved = h.StatusID === CLOSED_STATUS;
        const escalation = parseEscalation(h.Notes ?? "");

        return (
          <div key={h.ID} className={styles.item}>
            <span
              className={[styles.marker, resolved ? styles.markerResolved : escalation ? styles.markerEscalated : ""].filter(Boolean).join(" ")}
            >
              {resolved ? (
                <StatusIcon kind="solved" size={14} label="Resolved" />
              ) : escalation ? (
                <StatusIcon kind="push" size={13} label="Escalated" />
              ) : null}
            </span>

            <div className={styles.content}>
              <div className={styles.header}>
                {statusBadge(h.StatusID)}
                {resolved && <span className={styles.resolvedPill}>Resolved</span>}
                <span className={styles.author}>
                  {resolved ? <>Resolved by <strong>{h.CreatorFullName}</strong></> : h.CreatorFullName}
                </span>
                <span className={styles.date}>{formatDate(h.Changed)}</span>
              </div>

              {escalation ? (
                <div className={styles.escalationBody}>
                  Escalated to Support ticket{" "}
                  <a href={escalation.url} target="_blank" rel="noreferrer">
                    #{escalation.ticketNumber} ↗
                  </a>
                </div>
              ) : (
                h.Notes && <div className={styles.notes}>{h.Notes}</div>
              )}

              {h.AddedToSolution && (
                <div className={styles.solutionChip}>
                  <StatusIcon kind="check" size={12} />
                  Added to solution
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
